/**
 * ISO 8601 week-number helper — milestone v1.4 / bug-5 (CohortHeatmap labels).
 *
 * Background:
 *   The cohort heatmap used to label rows "Apr 06" and columns "W{offset}"
 *   where `offset` was the cohort-relative week index (0 = join week). That
 *   broke two expectations:
 *     1. Row labels ("Mon DD") are wider than a single column header,
 *        forcing the grid to push 32 px of left padding on every cell.
 *     2. Column headers "W0..W11" carried no absolute time information —
 *        analysts had to mentally add the cohort week to the offset to
 *        know which calendar week a cell referred to.
 *
 * Fix:
 *   Label rows and columns with ISO 8601 week numbers ("W15") so every
 *   axis speaks the same short, absolute unit. ISO weeks start on Monday
 *   and the first week of the year is the one containing that year's
 *   first Thursday (ISO 8601 §4.3.3). We compute them in UTC to match the
 *   rest of the dashboard's server-side date-trunc('week', …) buckets.
 *
 * Reference:
 *   https://en.wikipedia.org/wiki/ISO_week_date
 *   Algorithm: "Calculating the week number from an ordinal date" — the
 *   canonical integer form that avoids any TZ dependence.
 */

/**
 * Return the ISO 8601 week number (1..53) for a UTC date.
 *
 * Invariant: for every Monday in a given ISO week, `getIsoWeek(monday)`
 * returns the same integer as `getIsoWeek(anyOtherDayInThatWeek)`.
 *
 * @param d - A `Date` — the UTC components are used; any local TZ offset
 *   is ignored so the result matches backend `date_trunc('week', …)` output.
 * @returns The ISO 8601 week number (1..53). Returns `NaN` for an invalid
 *   date (we avoid throwing because the only call site renders a label —
 *   a `NaN` there is easier to debug than a hard error swallowing a render).
 */
export function getIsoWeek(d: Date): number {
  if (Number.isNaN(d.getTime())) return Number.NaN;
  // Copy to avoid mutating the caller's Date.
  const target = new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  );
  // ISO weekday: Monday=1 .. Sunday=7 (JS Sunday=0 so we remap to 7).
  const dayNum = target.getUTCDay() === 0 ? 7 : target.getUTCDay();
  // Shift to the Thursday of the current ISO week — that's the day that
  // determines which year the week belongs to.
  target.setUTCDate(target.getUTCDate() + 4 - dayNum);
  // The first Thursday of `target`'s year is in week 1 by definition.
  const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
  const daysFromYearStart =
    (target.getTime() - yearStart.getTime()) / (24 * 3600 * 1000);
  return Math.ceil((daysFromYearStart + 1) / 7);
}

/**
 * Format a Date (or ISO string) as an ISO-week label: "W{1..53}".
 *
 * Accepts both a `Date` object and a string so call sites can pass the raw
 * `cohortWeek` value from the API (`"YYYY-MM-DD"`) without parsing first.
 * Invalid / malformed strings fall through to the raw input — never an
 * empty label.
 *
 * @example
 *   isoWeekLabel('2026-01-05') // "W2" (first Monday in week 2, 2026)
 *   isoWeekLabel('2026-04-06') // "W15"
 */
export function isoWeekLabel(value: Date | string): string {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) {
    return typeof value === 'string' ? value : '';
  }
  return `W${getIsoWeek(d)}`;
}

/**
 * Build a short date-range tooltip for a cohort (Monday-to-Sunday in UTC).
 * Used by CohortHeatmap to disambiguate the ISO-week label when users hover
 * the row header: "W15 · Apr 06 – Apr 12".
 *
 * Sentinel `'older'` short-circuits to the sentinel label so we don't hand
 * a bogus Date to the formatter.
 */
export function isoWeekRangeTooltip(cohortWeek: string): string {
  if (cohortWeek === 'older') return 'Cohorts older than the visible window';
  const start = new Date(cohortWeek);
  if (Number.isNaN(start.getTime())) return cohortWeek;
  const end = new Date(start.getTime());
  end.setUTCDate(end.getUTCDate() + 6);
  const fmt = (x: Date): string => {
    // Render week-range dates in CET (Europe/Berlin) to match the rest of
    // the UI — user request 2026-04-24. Underlying week boundary math stays
    // in UTC (backend `date_trunc('week', ...)` is UTC-aligned).
    const month = x.toLocaleString('en-US', {
      month: 'short',
      timeZone: 'Europe/Berlin',
    });
    const day = x.toLocaleString('en-US', {
      day: '2-digit',
      timeZone: 'Europe/Berlin',
    });
    return `${month} ${day}`;
  };
  return `${isoWeekLabel(start)} · ${fmt(start)} – ${fmt(end)}`;
}
